import { zodResolver } from "@hookform/resolvers/zod"
import { Dialog, DialogActions, DialogContent } from "@mui/material"
import { type FC, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { useCreateGoodsReceiptPallets } from "../../hooks"
import type { SoftReceivedPurchaseOrdersFormValues } from "../../schemas"
import { createSoftReceivedPurchaseOrdersFormSchema } from "../../schemas"
import { getSoftReceivedPurchaseOrdersFormDefaultValues, serializeGoodsReceiptPalletsCreateForm } from "../../utils"

import { GoodsReceiptPalletsCreateForm } from "./GoodsReceiptPalletsCreateForm"

import { Button } from "@/components/common/Button"
import { CancelDialog } from "@/components/common/Modals"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import type { GoodsReceipt } from "@/graphql/codegen/graphql"
import { useAppView } from "@/hooks/useAppView"
import { classNames } from "@/utils/utils"

interface GoodsReceiptPalletsCreateDialogProps {
  goodsReceipt: GoodsReceipt
  onClose: () => void
  onSubmit: () => void
}

export const GoodsReceiptPalletsCreateDialog: FC<GoodsReceiptPalletsCreateDialogProps> = ({
  goodsReceipt,
  onClose,
  onSubmit,
}) => {
  const { isTabletView } = useAppView()
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const { create, isCreating } = useCreateGoodsReceiptPallets({ onSuccess: onSubmit })

  const methods = useForm<SoftReceivedPurchaseOrdersFormValues>({
    resolver: zodResolver(createSoftReceivedPurchaseOrdersFormSchema(true)),
    defaultValues: getSoftReceivedPurchaseOrdersFormDefaultValues(goodsReceipt, [], false),
    mode: "all",
  })

  const {
    handleSubmit,
    formState: { isValid, isDirty, isSubmitting },
  } = methods

  const hasAtLeastOnePallet = methods
    .watch("purchaseOrders")
    .some((po) => po.lines.some((line) => line.palletRows.some((row) => row.lots.length > 0)))

  const canSubmit = isValid && hasAtLeastOnePallet && !isSubmitting && !isCreating

  const handleClose = () => {
    if (isDirty) {
      setIsCancelDialogOpen(true)
    } else {
      onClose()
    }
  }

  const handleCancelDialogClose = () => {
    setIsCancelDialogOpen(false)
  }

  const handleCancelConfirm = () => {
    onClose()
  }

  const submitForm = async (formValues: SoftReceivedPurchaseOrdersFormValues) => {
    const input = serializeGoodsReceiptPalletsCreateForm(formValues)
    await create(input)
  }

  return isCancelDialogOpen ? (
    <CancelDialog onClose={handleCancelDialogClose} onConfirm={handleCancelConfirm} />
  ) : (
    <Dialog open onClose={onClose} maxWidth={!isTabletView && "xl"} fullWidth={!isTabletView} fullScreen={isTabletView}>
      <DialogHeader title='Add Pallets' onClose={onClose} />
      <form className='flex flex-1 flex-col' onSubmit={handleSubmit(submitForm)}>
        <DialogContent className='flex flex-col gap-6 p-10'>
          <FormProvider {...methods}>
            <GoodsReceiptPalletsCreateForm goodsReceipt={goodsReceipt} />
          </FormProvider>
        </DialogContent>
        <DialogActions className='flex w-full justify-between gap-4 px-10 pb-6'>
          <Button
            appearance='outlined'
            variant='primary'
            className={classNames({ "flex-1": isTabletView })}
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            type='submit'
            variant='primary'
            className={classNames({ "flex-1": isTabletView })}
            disabled={!canSubmit}
          >
            Confirm & Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
